import { getCurrentUser, signOut } from "aws-amplify/auth";
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../util/api/callApi";
import ApiModal, { API_BASE_URL, API_DOCUMENT_URL, ApiAuthStatus } from "./ApiModal";
import { formatJson, parseInput } from "./jsonUtils";
import ObjectTree from "./ObjectTree";

interface SwaggerEndpoint {
    description: string;
    method: string;
    parameters: SwaggerParameter[];
    path: string;
    summary: string;
    tags: string[];
}

interface SwaggerParameter {
    description?: string;
    in?: string;
    name?: string;
    required?: boolean;
    schema?: SwaggerSchema;
    type?: string;
}

interface SwaggerSchema {
    $ref?: string;
    description?: string;
    items?: SwaggerSchema;
    properties?: Record<string, SwaggerSchema>;
    required?: string[];
    type?: string;
}

interface ApiFormField {
    description: string;
    key: string;
    label: string;
    location: string;
    required: boolean;
    type: string;
}

export interface ApiResponseInfo {
    endpoint: SwaggerEndpoint;
    formValues: Record<string, string>;
    method: string;
    path: string;
    summary: string;
}

const HTTP_METHODS = new Set(["get", "post", "put", "patch", "delete"]);

const getSwaggerEndpoints = (document: unknown): SwaggerEndpoint[] => {
    if (!document || typeof document !== "object") return [];

    const paths = (document as { paths?: unknown }).paths;
    if (!paths || typeof paths !== "object" || Array.isArray(paths)) return [];

    return Object.entries(paths as Record<string, unknown>).flatMap(([path, pathValue]) => {
        if (!pathValue || typeof pathValue !== "object" || Array.isArray(pathValue)) return [];

        const pathDefinition = pathValue as Record<string, unknown>;
        const pathParameters = Array.isArray(pathDefinition.parameters)
            ? pathDefinition.parameters as SwaggerParameter[]
            : [];

        return Object.entries(pathValue as Record<string, unknown>).flatMap(([method, operationValue]) => {
            if (!HTTP_METHODS.has(method.toLowerCase()) || !operationValue || typeof operationValue !== "object") return [];

            const operation = operationValue as Record<string, unknown>;
            const operationParameters = Array.isArray(operation.parameters)
                ? operation.parameters as SwaggerParameter[]
                : [];
            const operationParameterKeys = new Set(operationParameters.map(parameter => `${parameter.in}.${parameter.name}`));

            return [{
                description: typeof operation.description === "string" ? operation.description : "",
                method: method.toUpperCase(),
                parameters: [
                    ...pathParameters.filter(parameter => !operationParameterKeys.has(`${parameter.in}.${parameter.name}`)),
                    ...operationParameters,
                ],
                path,
                summary: typeof operation.summary === "string" ? operation.summary : "",
                tags: Array.isArray(operation.tags) ? operation.tags.filter((tag): tag is string => typeof tag === "string") : [],
            }];
        });
    }).sort((first, second) => first.path.localeCompare(second.path) || first.method.localeCompare(second.method));
};

const resolveSwaggerSchema = (schema: SwaggerSchema | undefined, document: unknown): SwaggerSchema | undefined => {
    if (!schema?.$ref) return schema;
    if (!document || typeof document !== "object") return schema;

    const definitionName = schema.$ref.split("/").pop();
    const definitions = (document as { definitions?: Record<string, SwaggerSchema> }).definitions;
    return definitionName && definitions?.[definitionName] ? definitions[definitionName] : schema;
};

const getApiFormFields = (endpoint: SwaggerEndpoint | null, document: unknown): ApiFormField[] => {
    if (!endpoint) return [];

    const documentedPathParameters = new Set(
        endpoint.parameters
            .filter(parameter => parameter.in === "path")
            .map(parameter => parameter.name),
    );
    const inferredPathParameters: SwaggerParameter[] = Array.from(endpoint.path.matchAll(/\{([^}]+)\}/g))
        .map(match => match[1])
        .filter(name => !documentedPathParameters.has(name))
        .map(name => ({ in: "path", name, required: true, type: "string" }));
    const parameters = [...inferredPathParameters, ...endpoint.parameters];

    return parameters.flatMap((parameter, parameterIndex) => {
        const location = parameter.in || "parameter";

        if (location !== "body") {
            return [{
                description: parameter.description || "",
                key: `${location}.${parameter.name || parameterIndex}`,
                label: parameter.name || `Parameter ${parameterIndex + 1}`,
                location,
                required: Boolean(parameter.required),
                type: parameter.type || "string",
            }];
        }

        const bodySchema = resolveSwaggerSchema(parameter.schema, document);
        if (bodySchema?.properties) {
            const requiredFields = new Set(bodySchema.required || []);
            return Object.entries(bodySchema.properties).map(([name, propertySchema]) => {
                const resolvedProperty = resolveSwaggerSchema(propertySchema, document);
                return {
                    description: resolvedProperty?.description || propertySchema.description || "",
                    key: `body.${name}`,
                    label: name,
                    location,
                    required: requiredFields.has(name),
                    type: resolvedProperty?.type || propertySchema.type || (propertySchema.$ref ? "object" : "string"),
                };
            });
        }

        return [{
            description: bodySchema?.description || parameter.description || "",
            key: `body.${parameter.name || parameterIndex}`,
            label: parameter.name || "body",
            location,
            required: Boolean(parameter.required),
            type: bodySchema?.type || "object",
        }];
    });
};

const formatApiResponse = (response: unknown) => {
    if (typeof response === "string") {
        try {
            return formatJson(parseInput(response));
        } catch {
            return response;
        }
    }

    const serializedResponse = JSON.stringify(response);
    return serializedResponse === undefined ? String(response) : formatJson(JSON.parse(serializedResponse));
};

export const useApis = ({ activePageTab, changePageTab }: {
    activePageTab: string;
    changePageTab: (tab: "parse-object") => void;
}) => {
    const [apiEndpoints, setApiEndpoints] = useState<SwaggerEndpoint[]>([]);
    const [apiSearch, setApiSearch] = useState("");
    const [apiListError, setApiListError] = useState("");
    const [apiFormValues, setApiFormValues] = useState<Record<string, string>>({});
    const [apiAuthStatus, setApiAuthStatus] = useState<ApiAuthStatus>("checking");
    const [apiRequestError, setApiRequestError] = useState("");
    const [apiResponseInfo, setApiResponseInfo] = useState<ApiResponseInfo | null>(null);
    const [responseValue, setResponseValue] = useState("");
    const [responseVersion, setResponseVersion] = useState(0);
    const responseTree = useMemo(() => {
        try {
            return { value: responseValue ? parseInput(responseValue) : null, error: "" };
        } catch (error) {
            return { value: null, error: error instanceof Error ? error.message : "Invalid JSON" };
        }
    }, [responseValue]);
    const [isCallingApi, setIsCallingApi] = useState(false);
    const [isLoadingApis, setIsLoadingApis] = useState(false);
    const [selectedApiEndpoint, setSelectedApiEndpoint] = useState<SwaggerEndpoint | null>(null);
    const apiResponsesRef = useRef(new Map<string, { value: string; info: ApiResponseInfo }>());
    const selectedApiEndpointRef = useRef(selectedApiEndpoint);
    selectedApiEndpointRef.current = selectedApiEndpoint;
    const [swaggerDocument, setSwaggerDocument] = useState<unknown>(null);
    const filteredApiEndpoints = useMemo(() => {
        const query = apiSearch.trim().toLowerCase();

        if (!query) return apiEndpoints;
        return apiEndpoints.filter(endpoint => endpoint.path.toLowerCase().includes(query));
    }, [apiEndpoints, apiSearch]);
    const apiFormFields = useMemo(
        () => getApiFormFields(selectedApiEndpoint, swaggerDocument),
        [selectedApiEndpoint, swaggerDocument],
    );

    const loadApis = async () => {
        if (apiEndpoints.length || isLoadingApis) return;

        setIsLoadingApis(true);
        setApiListError("");

        try {
            const swaggerDocument = await api.get<unknown>(API_DOCUMENT_URL);
            const endpoints = getSwaggerEndpoints(swaggerDocument);

            setSwaggerDocument(swaggerDocument);
            setApiEndpoints(endpoints);
            if (!endpoints.length) setApiListError("No API endpoints were found in the Swagger document.");
        } catch {
            setApiListError("Unable to load APIs. Please make sure you are logged in and try again.");
        } finally {
            setIsLoadingApis(false);
        }
    };

    useEffect(() => {
        if (activePageTab !== "apis") return;
        let cancelled = false;
        const checkLogin = async () => {
            try {
                await getCurrentUser();
                if (cancelled) return;
                setApiAuthStatus("authenticated");
                await loadApis();
            } catch {
                if (!cancelled) setApiAuthStatus("unauthenticated");
            }
        };
        checkLogin();
        return () => { cancelled = true; };
        // Load once on tab entry; API state updates must not restart authentication.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activePageTab]);

    const handleApiLogin = async () => {
        setApiAuthStatus("authenticated");
        await loadApis();
    };

    const logoutFromApis = async () => {
        try {
            await signOut();
        } finally {
            setApiAuthStatus("unauthenticated");
            setApiEndpoints([]);
            apiResponsesRef.current.clear();
            setApiResponseInfo(null);
            setResponseValue("");
            setSwaggerDocument(null);
            setSelectedApiEndpoint(null);
            setApiFormValues({});
        }
    };

    const openApiForm = (endpoint: SwaggerEndpoint) => {
        const savedResponse = apiResponsesRef.current.get(`${endpoint.method} ${endpoint.path}`);
        setSelectedApiEndpoint(endpoint);
        setApiResponseInfo(savedResponse?.info ?? null);
        setApiFormValues(savedResponse ? { ...savedResponse.info.formValues } : {});
        setResponseValue(savedResponse?.value ?? "");
        setResponseVersion(version => version + 1);
        setApiRequestError("");
    };

    const callSelectedApi = async (
        endpoint = selectedApiEndpoint,
        formValues = apiFormValues,
    ) => {
        if (!endpoint) return;

        const requestFields = getApiFormFields(endpoint, swaggerDocument);
        const missingField = requestFields.find(field => field.required && !formValues[field.key]?.trim());
        if (missingField) {
            setApiRequestError(`${missingField.label} is required.`);
            return;
        }

        setIsCallingApi(true);
        setApiRequestError("");

        try {
            let endpointPath = endpoint.path;

            requestFields.filter(field => field.location === "path").forEach(field => {
                const value = formValues[field.key];
                if (value) endpointPath = endpointPath.replace(`{${field.label}}`, encodeURIComponent(value));
            });

            const requestUrl = new URL(`${API_BASE_URL}${endpointPath}`);
            requestFields.filter(field => field.location === "query").forEach(field => {
                const value = formValues[field.key];
                if (value) requestUrl.searchParams.set(field.label, value);
            });

            const requestBody = requestFields.filter(field => field.location === "body").reduce<Record<string, unknown>>((body, field) => {
                const value = formValues[field.key];
                if (!value) return body;

                if (field.type === "object" || field.type === "array") body[field.label] = JSON.parse(value);
                else if (field.type === "boolean") body[field.label] = value === "true";
                else if (field.type === "integer" || field.type === "number") body[field.label] = Number(value);
                else body[field.label] = value;
                return body;
            }, {});
            const hasRequestBody = Object.keys(requestBody).length > 0;
            let response: unknown;

            switch (endpoint.method) {
                case "GET":
                    response = await api.get<unknown>(requestUrl.toString());
                    break;
                case "POST":
                    response = await api.post<unknown>(requestUrl.toString(), requestBody);
                    break;
                case "PUT":
                    response = await api.put<unknown>(requestUrl.toString(), requestBody);
                    break;
                case "DELETE":
                    response = hasRequestBody
                        ? await api.deleteBody<unknown>(requestUrl.toString(), requestBody)
                        : await api.delete<unknown>(requestUrl.toString());
                    break;
                default:
                    throw new Error(`Unsupported method: ${endpoint.method}`);
            }
            const nextValue = formatApiResponse(response);

            const responseInfo: ApiResponseInfo = {
                endpoint,
                formValues: { ...formValues },
                method: endpoint.method,
                path: endpoint.path,
                summary: endpoint.summary,
            };
            apiResponsesRef.current.set(`${endpoint.method} ${endpoint.path}`, { value: nextValue, info: responseInfo });
            const selectedEndpoint = selectedApiEndpointRef.current;
            if (selectedEndpoint?.method === endpoint.method && selectedEndpoint.path === endpoint.path) {
                setResponseValue(nextValue);
                setResponseVersion(version => version + 1);
                setApiResponseInfo(responseInfo);
            }
            // Keep the selected API available while its response is shown alongside it.
        } catch (requestError) {
            const errorData = requestError && typeof requestError === "object" && "data" in requestError
                ? (requestError as { data?: unknown }).data
                : undefined;
            const errorMessage = requestError instanceof Error ? requestError.message : "";
            setApiRequestError(typeof errorData === "string" ? errorData : errorMessage || "Unable to call this API. Please check the input values and try again.");
        } finally {
            setIsCallingApi(false);
        }
    };

    const render = () => (
        <div className={activePageTab === "apis" ? "flex min-h-0 flex-1 flex-col gap-3 sm:grid sm:grid-cols-1 lg:grid-cols-2" : "hidden"}>
            <ApiModal
                embedded
                apiFormFields={apiFormFields}
                apiFormValues={apiFormValues}
                apiListError={apiListError}
                apiRequestError={apiRequestError}
                apiSearch={apiSearch}
                authStatus={apiAuthStatus}
                callSelectedApi={() => callSelectedApi()}
                endpointCount={apiEndpoints.length}
                filteredApiEndpoints={filteredApiEndpoints}
                handleApiLogin={handleApiLogin}
                isCallingApi={isCallingApi}
                isLoadingApis={isLoadingApis}
                isOpen={activePageTab === "apis"}
                loadApis={loadApis}
                onClose={() => { changePageTab("parse-object"); setSelectedApiEndpoint(null); }}
                onLogout={logoutFromApis}
                openApiForm={openApiForm}
                selectedApiEndpoint={selectedApiEndpoint}
                setApiEndpoints={setApiEndpoints}
                setApiFormValues={setApiFormValues}
                setApiSearch={setApiSearch}
                setSelectedApiEndpoint={setSelectedApiEndpoint}
            />
            {activePageTab === "apis" && (
                <ObjectTree
                    activePageTab="apis"
                    error={responseTree.error}
                    hasApiResponse={Boolean(apiResponseInfo)}
                    hasSelectedApiEndpoint={Boolean(selectedApiEndpoint)}
                    input={responseValue}
                    isCallingApi={isCallingApi}
                    isParsing={false}
                    mobilePanel="tree"
                    parsedValue={responseTree.value}
                    treeVersion={responseVersion}
                />
            )}
        </div>
    );
    const selectedApiResponseInfo = !selectedApiEndpoint || (apiResponseInfo?.method === selectedApiEndpoint.method
        && apiResponseInfo?.path === selectedApiEndpoint.path) ? apiResponseInfo : null;
    return { apiResponseInfo: selectedApiResponseInfo, setApiResponseInfo, isCallingApi, selectedApiEndpoint, render };
};
